/**
 * Daily offer API — narrow client over `/v1/daily/*`.
 *
 * Phase 3.1: GET daily offer (stack).
 * Phase 3.2: POST swipe + POST undo (right-swipe approve + 5s undo window).
 * Phase 3.4: POST auto-approve + POST skip.
 * Phase 3.7: one explicit regeneration turn + skipped-card review pool.
 *
 * The server builds a 5-card opening stack per turn (+ replacement cards on
 * reject up to 10 offers/turn, 2 turns/day) and caches it in `daily_offers`
 * for the rest of the day. First request of the day for a given
 * child = on-demand compute; subsequent reads = cache hit (instant).
 *
 * X-Device-Id is auto-attached by apiFetch.
 */

import { apiFetch } from './client';
import type { RejectReason, SwipeDirection } from '@funpaper/shared';
import type { DailyStack, WorksheetCard } from '@/types/daily';

// ----------------------------------------------------------------------------
// GET /v1/daily/:child_id
// ----------------------------------------------------------------------------

type DailyOfferResponse = {
  ok: true;
  offerId: number | null;
  offerDate: string;
  algoVersion: string;
  isCached: boolean;
  count: number;
  cards: DailyStack['cards'];
  approvedCount?: number;
  approveLimit?: number;
  /** true bila akun punya entitlement premium (server-side, dari daily GET). */
  premium?: boolean;
  turnsUsed?: number;
  maxTurns?: number;
  offersShown?: number;
  maxOffersPerDay?: number;
  canRegenerate?: boolean;
  reviewAvailable?: boolean;
  limitReached?: boolean;
  message?: string;
};

/**
 * Fetch the daily offer stack for a child.
 *
 * @param childId  Child profile id (from `getActiveChild().id`)
 * @returns The full stack with `cards` array + algorithm metadata.
 *          Empty `cards` is valid — server returns `{ count: 0, message }`
 *          when the candidate pool is exhausted.
 */
export async function getDailyOffer(childId: number): Promise<DailyStack> {
  const response = await apiFetch<DailyOfferResponse>(
    `/v1/daily/${childId}`,
  );
  return mapDailyStack(response);
}

function mapDailyStack(response: DailyOfferResponse): DailyStack {
  return {
    offerId: response.offerId,
    offerDate: response.offerDate,
    algoVersion: response.algoVersion,
    isCached: response.isCached,
    count: response.count,
    cards: response.cards,
    approvedCount: response.approvedCount ?? 0,
    approveLimit: response.approveLimit ?? 5,
    premium: response.premium ?? false,
    limitReached: response.limitReached ?? false,
    turnsUsed: response.turnsUsed ?? 1,
    maxTurns: response.maxTurns ?? 2,
    offersShown: response.offersShown ?? response.count,
    maxOffersPerDay: response.maxOffersPerDay ?? 20,
    canRegenerate: response.canRegenerate ?? false,
    reviewAvailable: response.reviewAvailable ?? false,
    message: response.message,
  };
}

// ----------------------------------------------------------------------------
// POST /v1/daily/:child_id/regenerate
// ----------------------------------------------------------------------------

export async function regenerateDailyOffer(childId: number): Promise<DailyStack> {
  const response = await apiFetch<DailyOfferResponse>(
    `/v1/daily/${childId}/regenerate`,
    { method: 'POST' },
  );
  return mapDailyStack(response);
}

// ----------------------------------------------------------------------------
// GET /v1/daily/:child_id/review
// ----------------------------------------------------------------------------

export async function getDailyReview(childId: number): Promise<WorksheetCard[]> {
  const response = await apiFetch<{ ok: true; cards: WorksheetCard[] }>(
    `/v1/daily/${childId}/review`,
  );
  return response.cards;
}

// ----------------------------------------------------------------------------
// POST /v1/daily/:child_id/reset
// Remote-dev only: clear today's test state so recommendations can be replayed.
// ----------------------------------------------------------------------------

export async function resetDailyRecommendations(childId: number): Promise<void> {
  await apiFetch<{ ok: true }>(
    `/v1/daily/${childId}/reset`,
    { method: 'POST' },
  );
}

// ----------------------------------------------------------------------------
// POST /v1/daily/:child_id/swipe
// Phase 3.2: right-swipe (approve) only. Left-swipe (reject) lands in 3.3
// with reject_reason + auto-swap.
// ----------------------------------------------------------------------------

export type PostSwipeInput = {
  childProfileId: number;
  worksheetId: number;
  direction: SwipeDirection;
  offerId?: number | null;
  velocity?: number;
  distance?: number;
  timeToDecideMs?: number;
  /** Required when direction === 'reject'. Phase 3.3 only. */
  rejectReason?: RejectReason;
  /** Free text when rejectReason === 'other'. */
  rejectReasonText?: string;
};

export type PostSwipeResult = {
  swipeLogId: number;
  /** Null when direction === 'reject' (no approved_worksheets row). */
  approvedWorksheetId: number | null;
  /** Pass to POST /undo to roll back within the 5s window. */
  undoToken: string;
  /** ISO timestamp; 5s after server received the swipe. */
  undoExpiresAt: string;
  undoWindowSeconds: number;
  /** Only set when direction === 'reject' and a replacement was found.
   *  Client appends to the cards array. */
  newWorksheet?: WorksheetCard | null;
  /** Reject-only: true bila seluruh putaran rekomendasi baru hari ini sudah
   *  selesai dan tidak ada kartu pengganti lagi. */
  offerCapReached?: boolean | null;
  /** True when the current 10-offer turn has no fresh card left. */
  turnExhausted?: boolean | null;
  /** True when the next daily recommendation turn can be started. */
  canRegenerate?: boolean;
  /** Current recommendation turn and offer counters from the server. */
  turnsUsed: number;
  maxTurns: number;
  offersShown: number;
  reviewAvailable: boolean;
  /** Server constants (Phase 3.6). */
  approveLimit: number;
  maxOffersPerDay: number;
  pickSource?: 'free' | 'credit' | null;
  creditBalance?: number | null;
};

type PostSwipeResponse = {
  ok: true;
  swipeLogId: number;
  approvedWorksheetId: number | null;
  undoToken: string;
  undoExpiresAt: string;
  undoWindowSeconds: number;
  newWorksheet?: WorksheetCard | null;
  offerCapReached?: boolean | null;
  turnExhausted?: boolean | null;
  canRegenerate?: boolean;
  turnsUsed: number;
  maxTurns: number;
  offersShown: number;
  reviewAvailable: boolean;
  approveLimit: number;
  maxOffersPerDay: number;
  pickSource?: 'free' | 'credit' | null;
  creditBalance?: number | null;
};

/**
 * Log a swipe event.
 *  - direction='approve' → writes to `approved_worksheets`, no new card.
 *    Server enforces the selected daily count (429 ApproveLimitReached).
 *  - direction='reject'  → server returns a `newWorksheet` replacement
 *    while the active 10-card turn still has room; after the turn ends the
 *    client can explicitly request the one remaining turn.
 *
 * Returns an `undoToken` valid for 5 seconds — call `postUndo` to roll
 * back. Undo works for both approve and reject; on reject-undo, the
 * `newWorksheet` is also rolled back (caller must remove it from the
 * local array).
 */
export async function postSwipe(input: PostSwipeInput): Promise<PostSwipeResult> {
  const response = await apiFetch<PostSwipeResponse>(
    `/v1/daily/${input.childProfileId}/swipe`,
    {
      method: 'POST',
      body: {
        childProfileId: input.childProfileId,
        worksheetId: input.worksheetId,
        direction: input.direction,
        // Optional fields must be omitted entirely (undefined), never sent
        // as explicit null — the server zod schema is .optional() without
        // .nullable(), so null would fail validation with HTTP 400.
        // JSON.stringify drops undefined-valued keys.
        offerId: input.offerId ?? undefined,
        velocity: input.velocity ?? undefined,
        distance: input.distance ?? undefined,
        timeToDecideMs: input.timeToDecideMs ?? undefined,
        rejectReason: input.rejectReason ?? undefined,
        rejectReasonText: input.rejectReasonText ?? undefined,
      },
    },
  );
  return {
    swipeLogId: response.swipeLogId,
    approvedWorksheetId: response.approvedWorksheetId,
    undoToken: response.undoToken,
    undoExpiresAt: response.undoExpiresAt,
    undoWindowSeconds: response.undoWindowSeconds,
    newWorksheet: response.newWorksheet ?? null,
    offerCapReached: response.offerCapReached ?? null,
    turnExhausted: response.turnExhausted ?? null,
    canRegenerate: response.canRegenerate ?? false,
    turnsUsed: response.turnsUsed ?? 1,
    maxTurns: response.maxTurns ?? 2,
    offersShown: response.offersShown ?? 0,
    reviewAvailable: response.reviewAvailable ?? false,
    approveLimit: response.approveLimit,
    maxOffersPerDay: response.maxOffersPerDay,
    pickSource: response.pickSource ?? null,
    creditBalance: response.creditBalance ?? null,
  };
}

// ----------------------------------------------------------------------------
// POST /v1/daily/:child_id/swipe/:swipe_id/reason
// Phase 3.6: backfill an OPTIONAL reject reason after the reject swipe was
// already logged (non-blocking banner flow). 409 ReasonAlreadySet if the
// user (or an earlier tap) already set it — safe to ignore on the client.
// ----------------------------------------------------------------------------

export type PostSwipeReasonInput = {
  childProfileId: number;
  swipeLogId: number;
  rejectReason: RejectReason;
  rejectReasonText?: string;
};

/**
 * Attach a reason to an already-logged reject swipe. Fire-and-forget from
 * the UI's perspective — failures should not disturb the swipe flow.
 */
export async function postSwipeReason(input: PostSwipeReasonInput): Promise<void> {
  await apiFetch<{ ok: true; swipeLogId: number }>(
    `/v1/daily/${input.childProfileId}/swipe/${input.swipeLogId}/reason`,
    {
      method: 'POST',
      body: {
        rejectReason: input.rejectReason,
        // Same omit-unset contract as postSwipe (never send explicit null).
        rejectReasonText: input.rejectReasonText ?? undefined,
      },
    },
  );
}


// Phase 3.2: rollback last approve swipe within 5s window.
// ----------------------------------------------------------------------------

export type PostUndoInput = {
  childProfileId: number;
  swipeLogId: number;
};

export type PostUndoResult = {
  swipeLogId: number;
  direction: SwipeDirection;
  ageSeconds: number;
};

type PostUndoResponse = {
  ok: true;
  restored: true;
  swipeLogId: number;
  direction: SwipeDirection;
  ageSeconds: number;
};

/**
 * Roll back a previous swipe within the 5s undo window.
 *
 * Server returns 410 Gone if the window expired, 409 Conflict if the
 * swipe was already undone. The caller (home screen) should:
 *  - 410 → silently rewind the local card stack to the previous index
 *  - 409 → toast "Sudah dibatalkan"
 *  - success → also rewind the local card stack
 */
export async function postUndo(input: PostUndoInput): Promise<PostUndoResult> {
  const response = await apiFetch<PostUndoResponse>(
    `/v1/daily/${input.childProfileId}/undo`,
    {
      method: 'POST',
      body: { swipeLogId: input.swipeLogId },
    },
  );
  return {
    swipeLogId: response.swipeLogId,
    direction: response.direction,
    ageSeconds: response.ageSeconds,
  };
}

// ----------------------------------------------------------------------------
// POST /v1/daily/:child_id/auto-approve
// Phase 3.4: escape hatch — "Biar Kami Pilih" approves all remaining
// cards in today's stack, up to user_preferences.dailyCount (default 3).
// Body: empty. Returns: { approvedCount, approvedWorksheetIds, cap }
// ----------------------------------------------------------------------------

export type PostAutoApproveResult = {
  approvedCount: number;
  approvedWorksheetIds: number[];
  cap: number;
  /** Server message when nothing was approved. */
  message?: string;
  creditBalance: number | null;
  creditRequired: { balance: number; cost: number } | null;
};

type PostAutoApproveResponse = {
  ok: true;
  approvedCount: number;
  approvedWorksheetIds: number[];
  cap: number;
  message?: string;
  creditBalance?: number | null;
  creditRequired?: { balance: number; cost: number } | null;
};

/**
 * Approve all remaining cards in today's stack (up to dailyCount).
 * Server writes to `approved_worksheets` AND logs a `direction='approve'`
 * row in `swipe_log` for each card, so the algorithm sees the escape
 * hatch as a strong positive signal.
 */
export async function postAutoApprove(childId: number): Promise<PostAutoApproveResult> {
  const response = await apiFetch<PostAutoApproveResponse>(
    `/v1/daily/${childId}/auto-approve`,
    { method: 'POST' },
  );
  return {
    approvedCount: response.approvedCount,
    approvedWorksheetIds: response.approvedWorksheetIds,
    cap: response.cap,
    message: response.message,
    creditBalance: response.creditBalance ?? null,
    creditRequired: response.creditRequired ?? null,
  };
}

// ----------------------------------------------------------------------------
// POST /v1/daily/:child_id/skip
// Phase 3.4: escape hatch — "Lewati hari ini" closes the home for the
// day, no streak penalty. Body: empty. Returns: { ok, skipped }
// ----------------------------------------------------------------------------

export type PostSkipResult = {
  skipped: true;
};

/**
 * Mark the day as skipped. No-op for the user's data — just a swipe_log
 * row with direction='drag_no_commit' for analytics. The client shows
 * a "Sampai besok!" empty state until the next day.
 */
export async function postSkip(childId: number): Promise<PostSkipResult> {
  await apiFetch<{ ok: true; skipped: true }>(
    `/v1/daily/${childId}/skip`,
    { method: 'POST' },
  );
  return { skipped: true };
}
